import { Fragment, type ReactNode } from 'react';
import { Box, Breadcrumbs, Container, Link as MuiLink, Typography } from '@mui/material';
import { JsonLd } from '@presentation/seo/JsonLd';
import { buildArticleGraph, buildBreadcrumb, buildFaqGraph } from '@presentation/seo/schema';
import type { BenefitStudy, BenefitTopic as BenefitTopicModel } from '@presentation/content/types';

type BenefitTopicProps = {
	topic: BenefitTopicModel;
};

// Turns inline [n] markers into clickable superscript links to the cited study,
// so every claim visibly references a real, verifiable source.
const withCitations = (text: string, studies: BenefitStudy[]): ReactNode[] =>
	text.split(/(\[\d+\])/g).map((part, index) => {
		const match = part.match(/^\[(\d+)\]$/);
		const study = match ? studies[Number(match[1]) - 1] : undefined;

		if (match && study) {
			return (
				<MuiLink
					key={index}
					href={study.url}
					target='_blank'
					rel='noopener noreferrer'
					sx={{ color: (theme) => theme.colors.brand, fontWeight: 600 }}
				>
					<sup>[{match[1]}]</sup>
				</MuiLink>
			);
		}

		return <Fragment key={index}>{part}</Fragment>;
	});

const BenefitTopic = ({ topic }: BenefitTopicProps) => {
	const path = `/benefits/${topic.slug}`;
	const updated = topic.generatedAt
		? new Date(topic.generatedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
		: '';

	return (
		<Container maxWidth='md' sx={{ py: { xs: 3, md: 5 } }}>
			<JsonLd data={buildArticleGraph(topic, path)} />
			{topic.faqs.length > 0 && <JsonLd data={buildFaqGraph(topic.faqs)} />}
			<JsonLd
				data={buildBreadcrumb([
					{ name: 'Home', url: '/' },
					{ name: 'Benefits', url: '/benefits' },
					{ name: topic.title },
				])}
			/>

			<Breadcrumbs sx={{ mb: 2, fontSize: 14 }}>
				<MuiLink
					href='/'
					sx={{
						color: (theme) => theme.colors.brand,
						textDecoration: 'none',
						'&:hover': { textDecoration: 'underline' },
					}}
				>
					Home
				</MuiLink>
				<MuiLink
					href='/benefits'
					sx={{
						color: (theme) => theme.colors.brand,
						textDecoration: 'none',
						'&:hover': { textDecoration: 'underline' },
					}}
				>
					Benefits
				</MuiLink>
				<Typography component='span' sx={{ fontSize: 14, color: 'text.secondary' }}>
					{topic.title}
				</Typography>
			</Breadcrumbs>

			<Typography component='h1' sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 800, mb: 2, textWrap: 'balance' }}>
				{topic.title}
			</Typography>

			<Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 3 }}>
				Reviewed by the Jigsaw Puzzle Universe editorial team · Based on {topic.studies.length} peer-reviewed studies
				{updated ? ` · Updated ${updated}` : ''}
			</Typography>

			{topic.intro && (
				<Typography
					component='p'
					sx={{ fontSize: { xs: 17, md: 19 }, lineHeight: 1.6, color: 'text.primary', mb: 3, textWrap: 'pretty' }}
				>
					{topic.intro}
				</Typography>
			)}

			{topic.summary.split('\n\n').map((paragraph, index) => (
				<Typography
					key={index}
					component='p'
					sx={{ fontSize: 16, lineHeight: 1.8, mb: 2, color: 'text.primary', textWrap: 'pretty' }}
				>
					{withCitations(paragraph, topic.studies)}
				</Typography>
			))}

			{topic.faqs.length > 0 && (
				<Box sx={{ mt: 4 }}>
					<Typography component='h2' sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 700, mb: 2 }}>
						Frequently asked questions
					</Typography>
					{topic.faqs.map((faq, index) => (
						<Box key={index} sx={{ mb: 2.5 }}>
							<Typography component='h3' sx={{ fontSize: 18, fontWeight: 600, mb: 0.5 }}>
								{faq.question}
							</Typography>
							<Typography component='p' sx={{ fontSize: 16, lineHeight: 1.7, color: 'text.primary' }}>
								{withCitations(faq.answer, topic.studies)}
							</Typography>
						</Box>
					))}
				</Box>
			)}

			<Box sx={{ mt: 4 }}>
				<Typography component='h2' sx={{ fontSize: { xs: 20, md: 22 }, fontWeight: 700, mb: 1.5 }}>
					Research references
				</Typography>
				<Box component='ol' sx={{ pl: 3, m: 0 }}>
					{topic.studies.map((study) => (
						<Box component='li' key={study.pmid} sx={{ mb: 1, fontSize: 15, lineHeight: 1.6 }}>
							<MuiLink
								href={study.url}
								target='_blank'
								rel='noopener noreferrer'
								sx={{ color: (theme) => theme.colors.brand }}
							>
								{study.title}
							</MuiLink>
							{` — ${study.journal}${study.year ? `, ${study.year}` : ''}`}
						</Box>
					))}
				</Box>
			</Box>

			<Box sx={{ mt: 4, pt: 3, borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
				<Typography component='h2' sx={{ fontSize: 16, fontWeight: 700, mb: 1 }}>
					How this article was created
				</Typography>
				<Typography component='p' sx={{ fontSize: 13, lineHeight: 1.7, color: 'text.secondary' }}>
					We searched PubMed for peer-reviewed studies published in the last 20 years, summarized their findings, and
					link every claim to its original source so you can verify it. AI tools assisted with drafting; the content was
					reviewed by the Jigsaw Puzzle Universe editorial team for accuracy. This article is general information about
					jigsaw puzzles, not medical advice — consult a qualified professional for health decisions.
				</Typography>
			</Box>
		</Container>
	);
};

export default BenefitTopic;
